import API from 'api';
import { useCallback } from 'react';
import { useFirebaseSignIn } from './useFirebaseSignIn';
import { useAuth0 } from '@auth0/auth0-react';
import { firebaseApp } from 'firebaseConfig';
import { getAuth } from 'firebase/auth';
import jwtDecode from 'jwt-decode';
import { useAuthorization } from './useAuth';
import useGoogleStorageHelpers from 'utils/helpers/googleStorage.helpers';

const TOKEN_REFRESH_BUFFER_MS = 1000 * 60 * 10; // 10 minutes

// the Auth0 user Firebase was last signed in as, so a different user never keeps the previous identity
const FIREBASE_AUTH0_SUB_KEY = 'firebase-signed-in-auth0-sub';

export const useRefreshFirebaseToken = () => {
  const { firebaseSignIn } = useFirebaseSignIn();
  const { gcpNetworkBlocked } = useGoogleStorageHelpers();
  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const { logout } = useAuthorization();

  const refreshFirebaseToken = useCallback(
    async (auth0Token = '') => {
      // if gcp getwork is blocked no firebase token is needed
      if (gcpNetworkBlocked) {
        console.debug('Use of CGP network is blocked or off: Skipping Firebase token refresh');
        return;
      }

      // if have token and not expired then do nothing
      const nowTime = new Date().getTime();
      const auth = getAuth(firebaseApp);
      const expirationTime: number = (auth.currentUser as any)?.stsTokenManager?.expirationTime || 0;
      if (expirationTime) {
        const expirationStr = 'Expiry at: ' + new Date(expirationTime).toISOString();
        const tokenValid = expirationTime - nowTime > TOKEN_REFRESH_BUFFER_MS;
        const signedInAsAuth0User = !auth0User?.sub || localStorage.getItem(FIREBASE_AUTH0_SUB_KEY) === auth0User.sub;
        if (tokenValid && signedInAsAuth0User) {
          console.debug('Firebase token still valid: Skipping refresh.', expirationStr);
          return;
        }
        console.debug('Firebase token expired, about to expire or for another user. Refreshing.', expirationStr);
      }

      // get the auth0 user sub/id
      if (!auth0Token) auth0Token = await getAccessTokenSilently();
      if (!auth0Token) {
        logout('inactivity');
        return;
      }
      const auth0UserId = auth0Token && jwtDecode<{ sub: string }>(auth0Token).sub;

      // get the firebase exchange token from local storage and check if expired
      const exchangeTokenStorageKey = `firebase-exchange-token-${btoa(auth0UserId)}`;
      let exchangeToken = localStorage.getItem(exchangeTokenStorageKey) || '';
      const exchangeTokenExpiryEpoch = (exchangeToken && +jwtDecode<{ exp: string }>(exchangeToken).exp) || 0;
      const exchangeTokenExpiryEpochMs = exchangeTokenExpiryEpoch * 1000;
      const exchangeTokenValid = exchangeTokenExpiryEpochMs - nowTime > TOKEN_REFRESH_BUFFER_MS;

      try {
        // update the firebase token if it is not valid
        const expirationStr = exchangeTokenExpiryEpochMs
          ? 'Expiry at: ' + new Date(exchangeTokenExpiryEpochMs).toISOString()
          : '';
        if (exchangeTokenValid) {
          console.debug('Firebase exchange token still valid: Skipping refresh.', expirationStr);
        } else {
          console.debug('Firebase exchange token absent, expired, or about to expire. Refreshing.', expirationStr);
          // this fetches a new firebase token and saves it to localStorage
          const user = await API.users.getCurrentUser(auth0UserId);
          exchangeToken = user.firebaseToken;
        }

        // refresh the firebase token
        await firebaseSignIn(exchangeToken, auth);
        localStorage.setItem(FIREBASE_AUTH0_SUB_KEY, auth0UserId);
        localStorage.setItem('gcpNetworkBlocked', '');
      } catch (error: any) {
        const errorMsg: string = error?.message || '';
        const isGcpNetworkError = errorMsg.includes('auth/network-request-failed');
        localStorage.setItem('gcpNetworkBlocked', isGcpNetworkError ? 'true' : '');
        if (isGcpNetworkError) {
          console.debug('Firebase token refresh failed: GCP network error. Skipping refresh.', errorMsg);
          return;
        }

        // non refreshable state
        API.errorHandler(error);
        logout('inactivity');
      }
    },
    [firebaseSignIn, gcpNetworkBlocked, getAccessTokenSilently, logout, auth0User?.sub],
  );

  return { refreshFirebaseToken };
};
